<template>
  <div class="search-page">
    <!-- 顶部搜索框 -->
    <div class="search-header">
      <div class="search-input-container">
        <div class="search-input-wrapper">
          <span class="search-icon">🔍</span>
          <input
            ref="searchInput"
            v-model="keyword"
            type="text"
            :placeholder="$t('customer.SearchPage.line12_placeholder')"
            class="search-input"
            autofocus
            @input="onSearch"
          />
          <span v-if="keyword" class="clear-btn" @click="clearKeyword">×</span>
        </div>
        <!-- 搜索下拉提示 -->
        <div v-if="keyword && suggestions.length > 0" class="search-suggestions-dropdown">
          <div
            v-for="(item, index) in suggestions"
            :key="index"
            class="suggestion-item"
            @click="selectSuggestion(item)"
          >
            <span class="suggestion-icon">🔍</span>
            <div class="suggestion-content">
              <div class="suggestion-name" v-html="highlightKeyword(item.name)"></div>
              <div class="suggestion-labels">
                <span v-if="item.aliases" class="label label-alias">
                  <span class="label-text">{{ $t('customer.SearchPage.line32_text') }}</span>
                  <span class="label-value" v-html="highlightKeyword(item.aliases)"></span>
                </span>
                <span v-if="item.tags" class="label label-tag">
                  <span class="label-text">{{ $t('customer.SearchPage.line36_text') }}</span>
                  <span class="label-value" v-html="highlightKeyword(item.tags)"></span>
                </span>
                <span v-if="item.categoryName" class="label label-category">
                  <span class="label-text">{{ $t('customer.SearchPage.line40_text') }}</span>
                  <span class="label-value">{{ item.categoryName }}</span>
                </span>
                <span v-if="item.subCategoryName" class="label label-subcategory">
                  <span class="label-text">{{ $t('customer.SearchPage.line44_text') }}</span>
                  <span class="label-value">{{ item.subCategoryName }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <span class="cancel-btn" @click="goBack">{{ $t('common.cancel') }}</span>
    </div>

    <!-- 搜索结果 -->
    <div v-if="keyword" class="search-results">
      <div v-if="searching" class="loading-state">{{ $t('customer.SearchPage.line57_text') }}</div>
      <div v-else-if="results.length === 0" class="empty-state">
        <div class="empty-icon">🔍</div>
        <p>{{ $t('customer.SearchPage.line60_text') }}</p>
      </div>
      <div v-else class="product-list">
        <div
          v-for="product in results"
          :key="product.id"
          class="product-item"
          @click="goToDetail(product.id)"
        >
          <img
            v-if="product.mainImage || product.image"
            :src="getImageUrl(product.mainImage || product.image)"
            class="product-image"
          />
          <div v-else class="product-image placeholder">📦</div>
          <div class="product-info">
            <div class="product-name">{{ product.name }}</div>
            <div class="product-tags" v-if="product.tags">
              <span v-for="tag in product.tags.split(',').slice(0, 3)" :key="tag" class="tag">
                {{ tag }}
              </span>
            </div>
            <div class="product-price">
              <span class="price">¥{{ product.marketPrice }}</span>
              <span class="unit">/{{ product.unit }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 未搜索时显示 -->
    <div v-else class="search-suggestions">
      <!-- 搜索历史 -->
      <div v-if="searchHistory.length > 0" class="section">
        <div class="section-header">
          <span>{{ $t('customer.SearchPage.line96_text') }}</span>
          <span class="clear-history" @click="clearHistory">{{ $t('customer.SearchPage.line97_text') }}</span>
        </div>
        <div class="history-tags">
          <span
            v-for="item in searchHistory"
            :key="item"
            class="history-tag"
            @click="searchKeyword(item)"
          >
            {{ item }}
          </span>
        </div>
      </div>

      <!-- 分类浏览 -->
      <div class="section">
        <div class="section-header">
          <span>{{ $t('customer.SearchPage.line114_text') }}</span>
          <span v-if="selectedCategory" class="back-btn" @click="selectedCategory = null">{{ $t('customer.SearchPage.line115_text') }}</span>
        </div>
        <!-- 一级分类标签 -->
        <div class="category-tabs" v-if="!selectedCategory">
          <div
            v-for="cat in categories"
            :key="cat.name"
            class="category-tab"
            @click="onCategoryClick(cat.name)"
          >
            <span>{{ cat.icon }} {{ cat.name }}</span>
          </div>
        </div>
        <!-- 二级分类标签 -->
        <div class="sub-category-tags" v-else>
          <div class="sub-category-title">{{ selectedCategory }}</div>
          <div
            v-for="sub in subCategories"
            :key="sub.subCategoryName"
            class="sub-tag"
            @click="onSubCategoryClick(sub.subCategoryName)"
          >
            {{ sub.subCategoryName }}
          </div>
          <div v-if="subCategories.length === 0" class="sub-tag all" @click="searchByCategory(selectedCategory)">
            查看全部「{{ selectedCategory }}」
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getImageUrl } from '@/utils/image'
import api from '@/services/api'

export default {
  name: 'SearchPage',

  data() {
    return {
      keyword: '',
      searching: false,
      results: [],
      searchHistory: [],
      categories: [],
      selectedCategory: null,
      subCategories: [],
      searchTimer: null,
      suggestions: [],
      suggestionTimer: null
    }
  },

  mounted() {
    this.loadSearchHistory()
    this.loadCategories()
    this.$nextTick(() => {
      this.$refs.searchInput?.focus()
    })
  },

  methods: {
    getImageUrl,

    // 加载分类列表
    async loadCategories() {
      try {
        const res = await api.get('/product/category/list')
        this.categories = Array.isArray(res) ? res : (res?.data || [])
      } catch (error) {
        console.error('加载分类失败:', error)
        this.categories = [
          { name: '水果类', icon: '🍎' },
          { name: '蔬菜类', icon: '🥬' },
          { name: '肉类', icon: '🥩' },
          { name: '水产类', icon: '🐟' },
          { name: '粮油类', icon: '🍚' }
        ]
      }
    },

    // 点击一级分类 - 展示二级分类
    async onCategoryClick(categoryName) {
      this.selectedCategory = categoryName
      try {
        const res = await api.get('/product/sub-categories', {
          params: { categoryName }
        })
        this.subCategories = Array.isArray(res) ? res : (res?.data || [])
      } catch (error) {
        console.error('加载二级分类失败:', error)
        this.subCategories = []
      }
    },

    // 搜索（防抖）
    onSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      this.searchTimer = setTimeout(() => {
        this.doSearch()
      }, 300)

      // 获取下拉提示（防抖）
      if (this.suggestionTimer) {
        clearTimeout(this.suggestionTimer)
      }
      this.suggestionTimer = setTimeout(() => {
        this.fetchSuggestions()
      }, 200)
    },

    // 获取搜索建议
    async fetchSuggestions() {
      const keyword = this.keyword.trim()
      if (!keyword || keyword.length < 1) {
        this.suggestions = []
        return
      }

      try {
        const res = await api.get('/product/search/suggestions', {
          params: { keyword, limit: 5 }
        })
        console.log('搜索建议响应:', res)
        // API 拦截器已解包 response.data，res 应该直接是数组
        // 但为了兼容，检查一下是否有 data 字段
        const data = Array.isArray(res) ? res : (res?.data || [])
        console.log('解析后的数据:', data)
        this.suggestions = data
      } catch (error) {
        console.error('获取建议失败:', error)
        this.suggestions = []
      }
    },

    // 高亮匹配关键词
    highlightKeyword(text) {
      if (!text || !this.keyword) return text
      const keyword = this.keyword.trim()
      if (!keyword) return text
      const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      return text.replace(regex, '<span class="highlight">$1</span>')
    },

    // 点击下拉建议 - 直接用商品名称搜索
    selectSuggestion(item) {
      this.suggestions = []
      this.$router.push({ path: '/stall/products', query: { keyword: item.name } })
    },

    // 点击二级分类 - 用二级分类搜索
    onSubCategoryClick(subCategoryName) {
      this.$router.push({ path: '/stall/products', query: { category: subCategoryName } })
    },

    // 只有一级分类时搜索
    searchByCategory(categoryName) {
      this.$router.push({ path: '/stall/products', query: { category: categoryName } })
    },

    // 执行搜索
    async doSearch() {
      const keyword = this.keyword.trim()
      const category = this.selectedCategory
      
      if (!keyword && !category) {
        this.results = []
        return
      }

      this.searching = true
      try {
        const customerId = localStorage.getItem('stored_customer_id')
        const res = await api.get('/customer/products', {
          params: { 
            customerId,
            keyword: keyword || undefined,
            category: category || undefined
          }
        })
        
        if (res && res.data) {
          this.results = Array.isArray(res.data) ? res.data : (res.data.items || res.data.content || [])
        } else if (Array.isArray(res)) {
          this.results = res
        } else {
          this.results = []
        }
        
        if (keyword) {
          this.saveToHistory(keyword)
        }
      } catch (error) {
        console.error('搜索失败:', error)
        this.results = []
      } finally {
        this.searching = false
      }
    },

    // 点击分类/历史 - 跳转到搜索结果页
    searchKeyword(keyword) {
      this.$router.push({ path: '/stall/products', query: { keyword } })
    },

    // 清空搜索词
    clearKeyword() {
      this.keyword = ''
      this.results = []
      this.suggestions = []
      this.$refs.searchInput?.focus()
    },

    // 加载搜索历史
    loadSearchHistory() {
      try {
        const history = localStorage.getItem('customer_search_history')
        this.searchHistory = history ? JSON.parse(history) : []
      } catch (e) {
        this.searchHistory = []
      }
    },

    // 保存到搜索历史
    saveToHistory(keyword) {
      let history = this.searchHistory.filter(item => item !== keyword)
      history.unshift(keyword)
      history = history.slice(0, 10)
      this.searchHistory = history
      localStorage.setItem('customer_search_history', JSON.stringify(history))
    },

    // 清空历史
    clearHistory() {
      this.searchHistory = []
      localStorage.removeItem('customer_search_history')
    },

    // 跳转商品详情
    goToDetail(productId) {
      this.$router.push(`/stall/product/${productId}`)
    },

    // 返回
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.search-page {
  min-height: 100vh;
  background: #f5f5f5;
  position: relative;
}

.search-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid #eee;
  overflow: visible;
}

.search-input-container {
  flex: 1;
  position: relative;
  z-index: 101;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 20px;
}

.search-icon {
  font-size: 14px;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  outline: none;
}

/* 搜索下拉提示 */
.search-suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 9999;
  max-height: 300px;
  overflow-y: auto;
  border-radius: 12px;
  margin-top: 4px;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.suggestion-item:active {
  background: #f5f5f5;
}

.suggestion-icon {
  font-size: 14px;
  color: #999;
}

.suggestion-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.suggestion-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.suggestion-name :deep(.highlight),
.label-value :deep(.highlight) {
  color: #ff4d4f;
  font-weight: 600;
  background: #fff7e6;
  padding: 0 2px;
  border-radius: 2px;
}

.suggestion-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  line-height: 1.4;
}

.label-text {
  color: rgba(0, 0, 0, 0.45);
}

.label-value {
  color: inherit;
  font-weight: 500;
}

.label-alias {
  background: #e6f7ff;
  color: #1890ff;
}

.label-tag {
  background: #f6ffed;
  color: #52c41a;
}

.label-category {
  background: #fff7e6;
  color: #fa8c16;
}

.label-subcategory {
  background: #f9f0ff;
  color: #722ed1;
}

.clear-btn {
  font-size: 18px;
  color: #999;
  cursor: pointer;
  padding: 0 4px;
}

.cancel-btn {
  color: #1890ff;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

/* 搜索结果 */
.search-results {
  padding: 12px;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.product-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.product-item:active {
  background: #f9f9f9;
}

.product-image {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.product-image.placeholder {
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
}

.product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.product-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.product-tags {
  display: flex;
  gap: 4px;
  margin-bottom: 6px;
}

.product-tags .tag {
  padding: 2px 6px;
  background: #f0f5ff;
  color: #2f54eb;
  font-size: 11px;
  border-radius: 3px;
}

.product-price {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.product-price .price {
  font-size: 16px;
  font-weight: 600;
  color: #ff4d4f;
}

.product-price .unit {
  font-size: 12px;
  color: #999;
}

/* 搜索建议 */
.search-suggestions {
  padding: 16px;
}

.section {
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.selected-tag {
  padding: 4px 10px;
  background: #1890ff;
  color: #fff;
  border-radius: 12px;
  font-size: 12px;
  font-weight: normal;
  cursor: pointer;
}

.selected-tag:active {
  opacity: 0.8;
}

.clear-history {
  font-size: 12px;
  color: #999;
  cursor: pointer;
}

.history-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.history-tag {
  padding: 6px 12px;
  background: #fff;
  border-radius: 16px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  transition: background 0.2s;
}

.history-tag:active {
  background: #f0f0f0;
}

/* 分类标签 */
.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.category-tab {
  padding: 6px 12px;
  background: #fff;
  border-radius: 16px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  border: 1px solid #eee;
  transition: all 0.2s;
}

.category-tab.active {
  background: #1890ff;
  color: #fff;
  border-color: #1890ff;
}

.category-tab:active {
  opacity: 0.8;
}

.sub-category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.sub-tag {
  padding: 5px 10px;
  background: #f0f5ff;
  border-radius: 12px;
  font-size: 12px;
  color: #2f54eb;
  cursor: pointer;
  transition: all 0.2s;
}

.sub-tag.all {
  background: #fff7e6;
  color: #fa8c16;
}

.sub-tag:active {
  opacity: 0.7;
}
</style>
